import { format } from 'svelte-i18n';
import { get } from 'svelte/store';

import type { VideoContainerState } from '../types/video-container';
import type { CameraStreamer } from '../types/camera-status';

const _format = get(format);

/**
 * Compute the camera's blocking state based on privacy, online status and
 * the streamer lifecycle. Returns a VideoContainerState when the stream
 * cannot be played (privacy / offline / streamer not running), or null
 * when the camera is ready to stream.
 *
 * Precedence (highest first):
 *   1. privacy → stream is disabled by user
 *   2. offline → device is unreachable
 *   3. streamer !== 'running' → pipeline is transitioning (starting /
 *      stopping / stopped); WebRTC cannot connect yet
 *
 * When this returns non-null, consumers should NOT mount the WebRTC player.
 *
 * @param privacyMode - Whether privacy mode is enabled
 * @param online - Whether the device is online
 * @param streamer - The `status['camera:0'].streamer` value from the device
 */
export function computeCameraState(
	privacyMode: boolean,
	online: boolean,
	streamer?: CameraStreamer | string,
): VideoContainerState {
	if (privacyMode) {
		return {
			type: 'privacy',
			icon: 'fas fa-video-slash',
			heading: _format('camera_privacy_mode', { default: 'Privacy Mode' }),
			message: _format('camera_privacy_mode_message', {
				default: 'Camera stream is disabled',
			}),
		};
	}
	if (!online) {
		return {
			type: 'offline',
			icon: 'fal fa-camera-web-slash',
			heading: _format('device_status_offline', { default: 'Device Offline' }),
		};
	}

	switch (streamer) {
		case 'starting':
			return {
				type: 'starting',
				heading: _format('camera_starting', { default: 'Starting camera' }),
				message: _format('camera_starting_message', {
					default: 'The camera stream is getting ready',
				}),
			};
		case 'stopping':
			return {
				type: 'starting',
				heading: _format('camera_stopping', { default: 'Stopping camera' }),
				message: _format('camera_stopping_message', {
					default: 'The camera stream is shutting down',
				}),
			};
		case 'stopped':
			return {
				type: 'starting',
				heading: _format('camera_stopped', { default: 'Camera stopped' }),
				message: _format('camera_stopped_message', {
					default: 'Waiting for the camera to start',
				}),
			};
		default:
			return null;
	}
}

/**
 * Compute the stream state from WebRTCPlayer's bound loading/error values.
 * Returns a VideoContainerState for loading/error, or null if streaming normally.
 *
 * IMPORTANT: Pass all values as arguments (not from closure) so Svelte's compiler
 * can track them as reactive dependencies in $: statements.
 *
 * @param loading - Whether the stream is loading
 * @param error - The stream error object (from WebRTCPlayer's errorMessage binding)
 */
export function computeStreamState(
	loading: boolean,
	error: { heading: string; message: string },
): VideoContainerState {
	if (loading) {
		return { type: 'loading', message: error.message };
	}
	if (error.heading) {
		return {
			type: 'error',
			heading: error.heading,
			message: error.message,
		};
	}
	return null;
}
